import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ConversationPanel } from "@/components/ConversationPanel";
import { ArtifactWorkspace } from "@/components/ArtifactWorkspace";
import { RightSidebar } from "@/components/RightSidebar";
import { useStreamingChat } from "@/hooks/useStreamingChat";
import { useNotificationSound } from "@/hooks/useNotificationSound";
import {
  ArrowLeft, Plus, MessageSquare, FileText, Activity,
  Loader2, Sparkles, CheckCircle2, CircleDot, Clock,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const stageColor = {
  completed: { bg: "bg-emerald-500", text: "text-emerald-600", light: "bg-emerald-50 border-emerald-200" },
  in_progress: { bg: "bg-[#014d7d]", text: "text-[#014d7d]", light: "bg-blue-50 border-[#014d7d]/20" },
  pending: { bg: "bg-gray-200", text: "text-foreground/50", light: "bg-gray-50 border-border/40" },
};
const stageIcon = {
  completed: <CheckCircle2 className="w-3 h-3 text-emerald-500" />,
  in_progress: <CircleDot className="w-3 h-3 text-[#014d7d]" />,
  pending: <Clock className="w-3 h-3 text-foreground/45" />,
};

export const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { playWaitingSound } = useNotificationSound();
  const { streamMessage } = useStreamingChat();

  // Lock parent <main> scroll so workspace fills viewport exactly
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) {
      const orig = main.className;
      main.className = orig.replace("overflow-y-auto", "overflow-hidden flex flex-col");
      return () => { main.className = orig; };
    }
  }, []);

  const [project, setProject] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [allArtifacts, setAllArtifacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [memoryItems, setMemoryItems] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [agentActivities, setAgentActivities] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("conversation");
  const [showNewSession, setShowNewSession] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [creatingSession, setCreatingSession] = useState(false);
  const [projectStages, setProjectStages] = useState([]);

  const hasInitRef = useRef(false);

  // Load project data
  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
        if (!data || typeof data !== 'object') return;
        setProject(data.project);
        setSessions(Array.isArray(data.sessions) ? data.sessions : []);
        setAllArtifacts(Array.isArray(data.artifacts) ? data.artifacts : []);
        setActivities(Array.isArray(data.activities) ? data.activities : []);
        setProjectStages(data.project?.workflow_stages || []);

        // If navigated from dashboard with autoSendGoal, load that session and auto-send
        const navState = location.state;
        if (navState?.autoSendGoal && navState?.sessionId && !hasInitRef.current) {
          hasInitRef.current = true;
          const targetSession = data.sessions.find(s => s.id === navState.sessionId) || data.sessions[0];
          if (targetSession) {
            setActiveSession(targetSession);
            setWorkflowSteps(targetSession.workflow_steps || []);
            setMemoryItems(targetSession.memory_items || []);
            // Auto-send the goal as first message
            setTimeout(() => sendStreamingMessage(targetSession, navState.autoSendGoal), 200);
          }
        } else if (data.sessions.length > 0 && !activeSession) {
          loadSession(data.sessions[0].id);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadSession = async (sessionId) => {
    try {
      const { data } = await axios.get(`${API}/sessions/${sessionId}`, { withCredentials: true });
      if (!data || typeof data !== 'object') return;
      setActiveSession(data.session);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setArtifacts(Array.isArray(data.artifacts) ? data.artifacts : []);
      setWorkflowSteps(data.session?.workflow_steps || []);
      setMemoryItems(data.session?.memory_items || []);
      setAgentActivities([]);
      setStreamingText("");
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateSession = async (e, directGoal) => {
    e.preventDefault();
    const goal = directGoal || newGoal.trim();
    if (!goal || creatingSession) return;
    setCreatingSession(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/sessions`, {
        goal, expertise_level: "intermediate",
      }, { withCredentials: true });
      if (!data || typeof data !== 'object' || !data.session) return;
      const newSession = data.session;
      setSessions((prev) => [newSession, ...prev]);
      setActiveSession(newSession);
      setMessages([]);
      setArtifacts([]);
      setWorkflowSteps(newSession.workflow_steps || []);
      setMemoryItems(newSession.memory_items || []);
      setShowNewSession(false);
      setTab("conversation");

      // Auto-send initial goal
      sendStreamingMessage(newSession, goal);
      setNewGoal("");
    } catch (e) {
      console.error(e);
    } finally {
      setCreatingSession(false);
    }
  };

  const sendStreamingMessage = useCallback(async (session, content) => {
    const s = session || activeSession;
    if (!s || isSending) return;
    setIsSending(true);
    setStreamingText("");
    setAgentActivities([{ agent: "Analyzing", action: "Processing...", status: "in_progress" }]);

    const tempMsg = {
      id: `temp-${Date.now()}`, session_id: s.id, role: "user", content,
      agent_activities: [], artifacts: [], created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);
    let accText = "";

    await streamMessage(`${API}/sessions/${s.id}/messages/stream`, { content, withCredentials: true }, {
      onUserMessage: (msg) => setMessages((prev) => [...prev?.filter((m) => m.id !== tempMsg.id), msg]),
      onAgentThinking: (a) => setAgentActivities((prev) => [...prev, a]),
      onAgentActivities: (a) => setAgentActivities(a),
      onTextChunk: (chunk) => { accText += chunk; setStreamingText(accText); },
      onComplete: (data) => {
        setStreamingText("");
        setMessages((prev) => [...prev, data.assistant_message]);
        if (data.assistant_message.artifacts?.length) {
          setArtifacts((prev) => [...prev, ...data.assistant_message.artifacts]);
          setAllArtifacts((prev) => [...prev, ...data.assistant_message.artifacts]);
        }
        if (data.workflow_steps) setWorkflowSteps(data.workflow_steps);
        if (data.memory_items) setMemoryItems(data.memory_items);
        if (data.assistant_message.agent_activities?.length) setAgentActivities(data.assistant_message.agent_activities);
        playWaitingSound();
      },
      onError: () => {
        setMessages((prev) => prev?.filter((m) => m.id !== tempMsg.id));
        setAgentActivities([]);
        setStreamingText("");
      },
      onDone: () => setIsSending(false),
    });
    setIsSending(false);
  }, [activeSession, isSending, streamMessage, playWaitingSound]);

  const handleSend = useCallback((content) => sendStreamingMessage(null, content), [sendStreamingMessage]);

  const handleRegenerate = useCallback(async (msgId) => {
    if (!activeSession || isSending) return;
    setIsSending(true);
    setStreamingText("");
    setAgentActivities([{ agent: "Regenerating", action: "Re-analyzing...", status: "in_progress" }]);
    let accText = "";

    await streamMessage(`${API}/sessions/${activeSession.id}/messages/${msgId}/regenerate/stream`, { withCredentials: true }, {
      onAgentThinking: (a) => setAgentActivities((prev) => [...prev, a]),
      onAgentActivities: (a) => setAgentActivities(a),
      onTextChunk: (chunk) => { accText += chunk; setStreamingText(accText); },
      onComplete: (data) => {
        setStreamingText("");
        if (data.messages) setMessages(data.messages);
        if (data.artifacts) { setArtifacts(data.artifacts); setAllArtifacts(data.artifacts); }
        if (data.workflow_steps) setWorkflowSteps(data.workflow_steps);
        if (data.memory_items) setMemoryItems(data.memory_items);
        playWaitingSound();
      },
      onError: () => { setAgentActivities([]); setStreamingText(""); },
      onDone: () => setIsSending(false),
    });
    setIsSending(false);
  }, [activeSession, isSending, streamMessage, playWaitingSound]);

  const handleArtifactUpdate = useCallback(async (id, content, title) => {
    try {
      const { data } = await axios.put(`${API}/artifacts/${id}`, { content, title }, { withCredentials: true });
      if (!data || typeof data !== 'object') return;
      setArtifacts((prev) => prev?.map((a) => (a.id === id ? data : a)));
      setAllArtifacts((prev) => prev?.map((a) => (a.id === id ? data : a)));
    } catch (e) { console.error(e); }
  }, []);

  const handleArtifactRevert = useCallback(async (id, version) => {
    try {
      const { data } = await axios.post(`${API}/artifacts/${id}/revert/${version}`, {}, { withCredentials: true });
      if (!data || typeof data !== 'object') return;
      setArtifacts((prev) => prev?.map((a) => (a.id === id ? data : a)));
      setAllArtifacts((prev) => prev?.map((a) => (a.id === id ? data : a)));
    } catch (e) { console.error(e); }
  }, []);

  const handleStartChatFromSelection = useCallback((text, artifact) => {
    // Send selected text as a chat message referencing the artifact
    const chatMsg = `Regarding "${artifact?.title || "this content"}":\n\n${text}`;
    sendStreamingMessage(null, chatMsg);
    setTab("conversation");
  }, [sendStreamingMessage]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  if (!project) {
    return <div className="p-8 text-center text-muted-foreground">Project not found</div>;
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden" data-testid="project-workspace">
      {/* Header */}
      <div className="h-12 border-b border-border/40 flex items-center px-4 gap-3 bg-white shrink-0 z-10">
        <button onClick={() => navigate("/")} className="text-foreground/50 hover:text-foreground" data-testid="back-to-projects">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-border/40" />
        <span className="font-heading font-semibold text-sm text-foreground truncate max-w-xs">{project.name}</span>

        {/* Session tabs */}
        <div className="ml-4 flex items-center gap-1 overflow-x-auto">
          {sessions?.map((s) => (
            <button
              key={s.id}
              onClick={() => loadSession(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                activeSession?.id === s.id ? "bg-cobalt/8 text-cobalt" : "text-foreground/40 hover:text-foreground hover:bg-foreground/[0.03]"
              }`}
              data-testid={`session-tab-${s.id}`}
            >
              {s.title?.slice(0, 25)}{s.title?.length > 25 ? "..." : ""}
            </button>
          ))}
          <button
            onClick={() => setShowNewSession(true)}
            className="px-2 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-slate-50"
            data-testid="new-session-btn"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* New Session Modal */}
      {showNewSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm" data-testid="new-session-modal">
          <div className="bg-white rounded-2xl border border-border/60 w-full max-w-md p-6 shadow-elevated">
            <h2 className="font-heading font-semibold text-lg mb-4">New Conversation</h2>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">What would you like to create?</label>
                <input
                  type="text" value={newGoal} onChange={(e) => setNewGoal(e.target.value)} required autoFocus
                  className="w-full h-10 px-3 text-sm border border-border rounded-md bg-white outline-none focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt"
                  data-testid="new-session-goal"
                  placeholder="e.g., Create a 6-week AI ethics course..."
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowNewSession(false)} className="flex-1 h-10 rounded-md border border-border text-sm font-medium">Cancel</button>
                <button type="submit" disabled={creatingSession} className="flex-1 h-10 rounded-md bg-cobalt text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="new-session-submit">
                  {creatingSession ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content — always show conversation layout */}
      {!activeSession && sessions.length === 0 ? (
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 relative min-h-0">
            <div className="absolute inset-0 grid grid-cols-12">
            {/* Conversation — auto-create session on first send */}
            <div className="col-span-4 border-r border-border flex flex-col bg-slate-50/50 overflow-hidden">
              <ConversationPanel
                messages={[]}
                onSend={(msg) => {
                  setNewGoal(msg);
                  handleCreateSession({ preventDefault: () => {} }, msg);
                }}
                onRegenerate={() => {}}
                isSending={creatingSession}
                streamingText=""
                sessionGoal={project?.description || ""}
                projectId={projectId}
                agentActivities={agentActivities}
              />
            </div>
            {/* Artifacts placeholder */}
            <div className="col-span-5 overflow-y-auto bg-white flex items-center justify-center">
              <div className="text-center px-6">
                <div className="w-12 h-12 rounded-xl bg-[#014d7d]/8 flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-5 h-5 text-[#014d7d]/40" />
                </div>
                <p className="text-xs text-foreground/50">Artifacts will appear here as you create content.</p>
              </div>
            </div>
            {/* Right Sidebar placeholder */}
            <div className="col-span-3 border-l border-border bg-white overflow-hidden">
              <RightSidebar agentActivities={[]} workflowSteps={[]} memoryItems={[]} />
            </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Workflow Stages Bar — with assets in each stage */}
          {projectStages.length > 0 && (
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white border-b border-border/40 shrink-0 overflow-x-auto" data-testid="workflow-stages-bar">
              <span className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mr-2 shrink-0">Workflow</span>
              {projectStages?.map((s, i) => {
                const sc = stageColor[s.status] || stageColor.pending;
                // Show artifact count from session artifacts grouped by stage
                const stageArtifacts = allArtifacts?.filter(a => {
                  const cat = (a.category || a.type || "").toLowerCase();
                  if (s.name === "Ingest") return cat.includes("source") || cat.includes("report");
                  if (s.name === "Audit") return cat.includes("audit") || cat.includes("report") || cat.includes("gap");
                  if (s.name === "Enrich") return cat.includes("assessment") || cat.includes("glossary") || cat.includes("mastery") || cat.includes("course");
                  if (s.name === "Review") return cat.includes("review") || cat.includes("approved");
                  if (s.name === "Publish") return cat.includes("publish") || cat.includes("package");
                  return false;
                });
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${sc.light} group relative`}>
                      {stageIcon[s.status]}
                      <span className={`text-[11px] font-semibold ${sc.text}`}>{s.name}</span>
                      <span className={`text-[10px] font-bold ${sc.text} ml-1 bg-white/60 rounded px-1`}>{s.artifacts || stageArtifacts.length}</span>
                    </div>
                    {i < projectStages.length - 1 && (
                      <div className={`w-4 h-px ${s.status === "completed" ? "bg-emerald-300" : "bg-border/40"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex-1 relative min-h-0">
          <div className="absolute inset-0 grid grid-cols-12">
          {/* Conversation */}
          <div className="col-span-4 border-r border-border flex flex-col bg-slate-50/50 overflow-hidden">
            <ConversationPanel
              messages={messages}
              onSend={handleSend}
              onRegenerate={handleRegenerate}
              isSending={isSending}
              streamingText={streamingText}
              sessionGoal={activeSession?.goal || ""}
              projectId={projectId}
              agentActivities={agentActivities}
            />
          </div>
          {/* Artifacts */}
          <div className="col-span-5 overflow-y-auto bg-white">
            <ArtifactWorkspace
              artifacts={artifacts}
              onUpdate={handleArtifactUpdate}
              onRevert={handleArtifactRevert}
              onStartChat={handleStartChatFromSelection}
              sessionGoal={activeSession?.goal || ""}
              apiBase={API}
            />
          </div>
          {/* Right Sidebar */}
          <div className="col-span-3 border-l border-border bg-white overflow-hidden">
            <RightSidebar
              agentActivities={agentActivities}
              workflowSteps={workflowSteps}
              memoryItems={memoryItems}
            />
          </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};
